import React from 'react';
import Barometer from './Barometer';

function DataTable({ 
  quarters, 
  groupedQuarters, 
  quarterlyData, 
  liggetidData, 
  afslagData, 
  forSaleData, 
  bnpData,
  monthlyData,
  calculateYearOverYearChange,
  calculateQuarterlyAverageRente,
  calculateYearOverYearInflation,
  calculateQuarterlyAverageUnemployment 
}) {
  const categories = [
    { key: 'boligpris', displayName: 'Salgspris' },
    { key: 'boligsalg', displayName: 'Solgte boliger' },
    { key: 'afslag', displayName: 'Prisafslag' },
    { key: 'liggetid', displayName: 'Liggetid' },
    { key: 'forSale', displayName: 'Boliger til salg' },
    { key: 'bnp', displayName: 'BNP' },
  ];

  const getScoreClass = (value, type) => {
    switch(type) {
      case 'salgspris':
      case 'boligsalg':
        if (value > 5) return 'best';
        if (value > 2) return 'second-best';
        if (value > -2) return 'second-worst';
        return 'worst';
      
      case 'liggetid':
      case 'forSale':
        if (value <= -5) return 'best';
        if (value < -2) return 'second-best';
        if (value < 2) return 'second-worst';
        return 'worst';
      
      case 'rente':
        if (value <= 2.0) return 'best';
        if (value <= 4.0) return 'second-best';
        if (value <= 6.0) return 'second-worst';
        return 'worst';
      
      case 'bnp':
        if (value >= 2.0) return 'best';
        if (value >= 1.0) return 'second-best';
        if (value >= 0.0) return 'second-worst';
        return 'worst';
      
      // Add other cases with matching thresholds
      default:
        return '';
    }
  };

  const TableRow = ({ category, quarters, data, calculateChange }) => {
    const displayName =
      category === 'boligsalg'
        ? 'Solgte boliger'
        : category === 'boligpris'
        ? 'Salgspris'
        : category === 'forSale'
        ? 'Boliger til salg'
        : category === 'afslag'
        ? 'Prisafslag'
        : category === 'liggetid'
        ? 'Liggetid'
        : category === 'bnp'
        ? 'BNP'
        : category.charAt(0).toUpperCase() + category.slice(1);

    return (
      <tr>
        <td>{displayName}</td>
        {quarters.map((quarter) => {
          let value;
          let cellClass = '';

          // Check if we have data for this quarter
          if (!data || !data[quarter]) {
            return <td key={quarter} className="na-cell">N/A</td>;
          }

          if (category === 'liggetid') {
            const change = calculateChange(data, quarter);
            value = change !== null ? change : null;

            if (value === null || value === undefined) {
              cellClass = 'na-cell';
            } else {
              if (change > 10) {
                cellClass = 'worst';
              } else if (change > 0 && change <= 10) {
                cellClass = 'second-worst';
              } else if (change < 0 && change >= -10) {
                cellClass = 'second-best';
              } else if (change < -10) {
                cellClass = 'best';
              }
            }
          } else if (category === 'forSale') {
            const change = calculateChange(data, quarter);
            value = change !== null ? change : null;

            if (value === null || value === undefined) {
              cellClass = 'na-cell';
            } else {
              if (change >= 10) {
                cellClass = 'worst';
              } else if (change >= 0 && change <= 9.9) {
                cellClass = 'second-worst';
              } else if (change <= -1 && change >= -9.9) {
                cellClass = 'second-best';
              } else if (change <= -10) {
                cellClass = 'best';
              }
            }
          } else if (category === 'afslag') {
            value = data[quarter];

            if (value === null || value === undefined) {
              cellClass = 'na-cell';
            } else {
              if (value >= 8.0) {
                cellClass = 'worst';
              } else if (value >= 5.1) {
                cellClass = 'second-worst';
              } else if (value > 3.5 && value <= 5.0) {
                cellClass = 'second-best';
              } else if (value <= 3.5) {
                cellClass = 'best';
              }
            }
          } else if (category === 'boligpris' || category === 'boligsalg') {
            const change = calculateChange(data, quarter);
            value = change !== null ? change : null;

            if (value === null || value === undefined) {
              cellClass = 'na-cell';
            } else {
              if (change > 10) {
                cellClass = 'best';
              } else if (change > 0 && change <= 10) {
                cellClass = 'second-best';
              } else if (change < 0 && change >= -10) {
                cellClass = 'second-worst';
              } else if (change < -10) {
                cellClass = 'worst';
              }
            }
          } else if (category === 'bnp') {
            value = data[quarter];

            if (value === null || value === undefined) {
              cellClass = 'na-cell';
            } else {
              if (value <= -1.5) {
                cellClass = 'worst';
              } else if (value >= -1.4 && value <= -0.1) {
                cellClass = 'second-worst';
              } else if (value >= 0 && value <= 1.4) {
                cellClass = 'second-best';
              } else if (value >= 1.5) {
                cellClass = 'best';
              }
            }
          }

          return (
            <td key={quarter} className={cellClass}>
              {value !== null ? `${value.toFixed(1)}%` : 'N/A'}
            </td>
          );
        })}
      </tr>
    );
  };

  // Add additional rows after the categories mapping
  const renderAdditionalRows = () => {
    return (
      <>
        {/* Rente Row */}
        <tr>
          <td>Rente</td>
          {quarters.map((quarter) => {
            const renteValue = calculateQuarterlyAverageRente(quarter);
            let cellClass = '';

            if (renteValue === null) {
              return <td key={quarter} className="na-cell">N/A</td>;
            }

            if (renteValue >= 0 && renteValue <= 3.0) {
              cellClass = 'best';
            } else if (renteValue >= 3.1 && renteValue <= 5.0) {
              cellClass = 'second-best';
            } else if (renteValue >= 5.1 && renteValue <= 8.0) {
              cellClass = 'second-worst';
            } else if (renteValue >= 8.1) {
              cellClass = 'worst';
            }

            return (
              <td key={quarter} className={cellClass}>
                {`${renteValue.toFixed(1)}%`}
              </td>
            );
          })}
        </tr>

        {/* Inflation Row */}
        <tr>
          <td>Inflation</td>
          {quarters.map((quarter) => {
            const inflationChange = calculateYearOverYearInflation(quarter);
            let cellClass = '';

            if (inflationChange === null) {
              return <td key={quarter} className="na-cell">N/A</td>;
            }

            if (inflationChange >= 0 && inflationChange <= 2.0) {
              cellClass = 'best';
            } else if (inflationChange >= 2.1 && inflationChange <= 3.0) {
              cellClass = 'second-best';
            } else if (inflationChange >= 3.1 && inflationChange <= 4.0) {
              cellClass = 'second-worst';
            } else if (inflationChange >= 4.1) {
              cellClass = 'worst';
            }

            return (
              <td key={quarter} className={cellClass}>
                {`${inflationChange.toFixed(1)}%`}
              </td>
            );
          })}
        </tr>

        {/* Unemployment Row */}
        <tr>
          <td>Ledighed</td>
          {quarters.map((quarter) => {
            const unemploymentValue = calculateQuarterlyAverageUnemployment(quarter);
            let cellClass = '';

            if (unemploymentValue === null) {
              return <td key={quarter} className="na-cell">N/A</td>;
            }

            if (unemploymentValue >= 0 && unemploymentValue <= 3.0) {
              cellClass = 'best';
            } else if (unemploymentValue > 3.0 && unemploymentValue <= 4.0) {
              cellClass = 'second-best';
            } else if (unemploymentValue > 4.0 && unemploymentValue <= 5.0) {
              cellClass = 'second-worst';
            } else if (unemploymentValue > 5.0) {
              cellClass = 'worst';
            }

            return (
              <td key={quarter} className={cellClass}>
                {`${unemploymentValue.toFixed(1)}%`}
              </td>
            );
          })}
        </tr>
      </>
    );
  };

  return (
    <div className="dataset-container">
      <Barometer 
        quarterlyData={quarterlyData}
        liggetidData={liggetidData}
        afslagData={afslagData}
        forSaleData={forSaleData}
        bnpData={bnpData}
        monthlyData={monthlyData}
        calculateQuarterlyAverageRente={calculateQuarterlyAverageRente}
        calculateYearOverYearInflation={calculateYearOverYearInflation}
        calculateQuarterlyAverageUnemployment={calculateQuarterlyAverageUnemployment}
      />
      
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>År</th>
              {Object.entries(groupedQuarters).map(([year, quartersList]) => (
                <th key={year} colSpan={quartersList.length}>
                  {year}
                </th>
              ))}
            </tr>
            <tr>
              <th>Kvartal</th>
              {Object.values(groupedQuarters).flat().map((quarter, index) => (
                <th key={index}>{quarter.slice(-1)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map(({ key, displayName }) => (
              <TableRow
                key={key}
                category={key}
                quarters={quarters}
                data={
                  key === 'boligpris' || key === 'boligsalg'
                    ? quarterlyData[key]
                    : key === 'liggetid'
                    ? liggetidData
                    : key === 'afslag'
                    ? afslagData
                    : key === 'forSale'
                    ? forSaleData
                    : key === 'bnp'
                    ? bnpData
                    : null
                }
                calculateChange={calculateYearOverYearChange}
              />
            ))}
            {renderAdditionalRows()}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DataTable; 